<template>
    <div class="mt-[20px] level-option">
        <div class="text-[#55657e] text-[15px] font-medium mb-[10px] sm:!hidden">
            {{ t.GameTower.DevilkinsNums }}:
        </div>
        <div class="grid grid-cols-2 gap-2 h-[47px]">
            <div class="inline-flex w-full">
                <DropDown 
                    v-if="dropDownOptions.length > 0" 
                    v-model="mutiplsOptions.value"
                    :options="dropDownOptions" 
                    @update:model-value="emit('update:numberDevilkins', Number($event))" 
                    class="h-[48px] w-full"
                    :disabled="disabled || isSubmiting"
                >
                    <template #prevIcon>
                        <span class="text-sm font-semibold text-[#55657e]">
                            {{ t.GameTower.Devilkins }}:
                        </span>
                    </template>
                </DropDown>
            </div>

            <div class="grid grid-cols-2 gap-2 sm:grid-cols-1">
                <button
                    @click="autoPick"
                    class="bg-[#ffb636] border-[#ffb636] h-full w-full rounded-md text-black select-none text-base font-normal"
                    :disabled="bDisableTheAutoButton"
                >
                    {{ t.GameTower.AutoPick }}
                </button>
                <button
                    @click="onCleanTable"
                    class="bg-[#1b2233] select-none h-full w-full rounded-md font-normal sm:!hidden"
                    :disabled="!isAuto || joinStateStore.gameRunning"
                >
                    {{ t.GameTower.Cleartable }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import useTranslation from '~/i18n/useTransLation';
import { GameStatus, type RoundStatus, dropDownOptions } from '../data';
import { useGameSocket } from '~/socket/game';
import { BetType } from '../../blackjackGame/ts/blackjackGame';
import { useJoinStateStore } from '~/stores/cansino/joinState';
const t = useTranslation();
const joinStateStore = useJoinStateStore();
const emit = defineEmits([
    'update:predictionSending',
    'update:numberDevilkins',
    'update:round',
    'update:winAmount',
    'update:savedPredictions',
    'update:fields',
    'update:isOpenResult',
    'reset'
]);
const { EMIT_TOWER_BET } = useGameSocket();

const mutiplsOptions = computed(() => {
    return {
        value: props.numberDevilkins,
        options: dropDownOptions,
    };
});

const props = defineProps({
    numberDevilkins: {
        type: Number,
        required: true,
    },
    round: {
        type: Object as PropType<RoundStatus>,
        required: true,
    },
    // fields: {
    //     type: Array,
    //     required: true,
    // },
    sounds: {
        type: [Object, null],
        default: () => ({}),
        required: true,
    },
    isAuto: {
        type: Boolean,
        required: true,
    },
    bet: {
        type: Function,
        required: true,
    },
    disabled: {
        type: Boolean,
        required: true,
    },
    savedPredictions: {
        type: Array as PropType<number[]>,
    },
    isSubmiting: {
        type: Boolean,
        required: true,
    },
    predictionSending: {
        type: Boolean,
        required: true,
    },
    isOpenResult: {
        type: Boolean,
        default: false
    },
    coefficients: {
        type: Object as PropType<any>,
        default: () => ({})
    },
    animateEnabled: {
        type: Boolean,
        required: true,
    },
});
const autoBtnDisabled = ref(false);

const bDisableTheAutoButton = computed(()=>{
    const round = props.round;
    const arr = [
        ['lose', !props.isAuto && '', 'win'].includes(round.status),
        props.isAuto && joinStateStore.gameRunning,
        (round.prediction.length === 10 || props.savedPredictions.length === 10),
        autoBtnDisabled.value
    ];
    return arr.some(Boolean); 
});

/**
 * 自动选择（点击按钮） 
 */
const autoPick = async () => {
    console.log(props, 'autoPick');
    if (props.round.status === 'lose') return;

    if (props.round.prediction.length === 10 || props.savedPredictions.length === 10) return;
    if (props.animateEnabled){
        autoBtnDisabled.value = true;
        setTimeout(() => {
            autoBtnDisabled.value = false;
        }, 330); // delay = 330 开启动画时，卡片反转动画执行完后才能再次点击
    }
    const boardIndex = Math.floor(Math.random() * 5);
    floorClick(props.round.prediction.length, boardIndex);
};

const addToPrediction = (targetLength, prediction) => {
    if (props.isAuto) {
        emit('update:savedPredictions', [...props.savedPredictions, prediction]);
        emit('update:round', {
            ...props.round,
            step: props.round.step + 1,
            level: props.round.level + 1,
        });
    } else {
        const predictions = props.round.prediction;
        const roundStatus = props.round.status;
        const roundLevel = props.round.level;

        // Check if the length of predictions array equals targetLength
        if (predictions.length === targetLength) {
            // Add the new prediction to the predictions array
            predictions.push(prediction);

            // Increment round level if predictions length is less than 10 and round status is not "lose"
            if (predictions.length < 10 && roundStatus !== 'lose') {
                props.round.level += 1;
            }
        } else {
            // Check if the last prediction is the same as the new prediction
            if (predictions[predictions.length - 1] === prediction) {
                // Decrease round level if greater than 1, else keep it unchanged
                props.round.level =
                    roundLevel > 1 ? (predictions.length === 10 ? 10 : roundLevel - 1) : 1;

                // Remove the last prediction from the predictions array
                predictions.pop();
            }
        }

        emit('update:round', { ...props.round, prediction: predictions });
    }
};

const openCell = async (targetLength, prediction) => {
    if (props.predictionSending){
        return console.log('禁止连点-自动按钮');
    }
    emit('update:predictionSending', true); // 上锁，以防止连点和突然结算
    try {
        // Call the bet function to open a cell
        const res: any = await EMIT_TOWER_BET({
            prediction,
            isFast: false,
            type: BetType.HandsStart,
            frontID: new Date().getTime(),
        });
        if (!res || res.error){
            showNegativeNotify(res?.error); 
            emit('update:predictionSending', false); 
            return; 
        }
        const { win, status, winAmount, fields } = res;

        addToPrediction(targetLength, prediction);

        if (winAmount) {
            emit('update:winAmount', winAmount);
        }
        if (!win) props.sounds?.oAudio.lose.toPlay();

        emit('update:fields', fields);
        emit('update:round', {
            ...props.round,
            ...res,
            // prediction: res.prediction,
            fields,
            multi: res.multi,
            status: !win || status === 'lose' ? 'lose' : 'cashout',
            step: props.round.step + 1,
        });
        emit('update:predictionSending', false); 
        // 成功登顶后自动提现
        if (fields?.length === 10 && status === 'cashout') {
            // 显示赢分动画并播放音效
            emit('update:isOpenResult', true);
            props.sounds?.oAudio.win.toPlay();
            await Sleep(props.coefficients.insertDelay);
            // joinStateStore.showDateInQueue(); // 插入游戏结果并播放动画（当前游戏没有延时，注释此行）
            await Sleep(800);
            emit('update:isOpenResult', false);
            emit('reset');
        }
    } catch (error) {
        console.error(error);
        emit('update:predictionSending', false);
    }
};

const floorClick = (t, boardIndex) => {
    props.sounds?.oAudio.simpleClick.toPlay();
    props.isAuto ? addToPrediction(t, boardIndex) : openCell(t, boardIndex);
};

const onCleanTable = () => {
    emit('update:savedPredictions', []);
    emit('update:fields', []);
    emit('update:round', {
        ...props.round,
        prediction: [],
        status: GameStatus.EMPTY,
        step: 0,
        level: 0,
    });
};
watch(
    () => props.round.status,
    async (newVal) => {
        if (newVal === 'lose') {
            joinStateStore.gameRunning = false;
            props.sounds?.oAudio.lose.toPlay();
            await Sleep(props.coefficients.insertDelay);
            // joinStateStore.showDateInQueue(); // 插入游戏结果并播放动画 （当前游戏没有延时，注释此行）
        }
    },
);
</script>

<style lang="scss" scoped>
.level-option {
    @media (max-width: 576px) {
        margin-top: 10px;
    }
}
.btn {
    background-color: #161f2c;
    border: 1px solid rgba(85, 101, 126, 0.28);
    border-radius: 10px;
    box-sizing: border-box;
    color: #55657e;
    display: block;
    font-size: 16px;
    font-weight: 600;
    height: 100%;
    left: 20px;
    line-height: 45px;
    margin-right: 9px;
    text-align: center;
    transition: all 0.3s;
    &:last-child {
        margin-right: 0px;
    }
}
.btnCurrent {
    border: 1px solid #297fe5;
    border-radius: 10px;
    box-sizing: border-box;
    color: #fff;
    display: block;
    font-size: 16px;
    font-weight: 600;
    height: 100%;
    left: 20px;
    line-height: 45px;
    margin-right: 9px;
    text-align: center;
    transition: all 0.3s;
    background-color: #297fe5;
}
</style>
